/**
 * PauseSolvePanel — Stacked 레이아웃 하단 full-width 풀이 영역.
 * QuizRenderer 재사용 · pending 답 보존 · inline 피드백.
 *
 * Spec: docs/superpowers/specs/2026-04-22-lc-pause-quiz-panel-redesign-design.md §7.4
 */
import { useCallback, useMemo, useRef, useState } from 'react'
import { QuizRenderer, QuizStyleProvider, QuizResultPopup } from '@quebon/shared-learning'
import type { QuizSolveResult } from '@quebon/shared-learning'
import { Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { LcQuizResultDescription, PauseAndGoPoint } from '@/api/lcLearning'
import { submitPauseQuizResponse } from '@/api/lcLearning'
import { gradeLibraryQuiz } from '@/api/lcLibraryReview'
import { getApiErrorMessage } from '@/utils/apiError'
import { Button } from '../ui/Button'
import type { RuntimeMode } from '../ComponentRunner'
import { PauseSubjectiveResultPopup } from './PauseSubjectiveResultPopup'

interface Props {
  point: PauseAndGoPoint
  pointIndex: number
  enrollmentId: number
  learningAttemptId?: number | null
  componentProgressId: number | null
  runtimeMode?: RuntimeMode
  isDesignatedReview?: boolean
  initialAnswers?: string[]
  onAnswerChange: (answers: string[]) => void
  onSubmitSuccess: (quizId: string) => void
  onResume: () => void
}

interface SubmittedState {
  correct: boolean | null
  correctAnswers: string[]
  description: LcQuizResultDescription | null
}

export default function PauseSolvePanel({
  point,
  pointIndex,
  enrollmentId,
  learningAttemptId,
  componentProgressId,
  runtimeMode = 'live',
  isDesignatedReview = false,
  initialAnswers,
  onAnswerChange,
  onSubmitSuccess,
  onResume,
}: Props) {
  const isLive = runtimeMode === 'live'
  const { t } = useTranslation('learner')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState<SubmittedState | null>(null)
  const [pending, setPending] = useState<string[]>(initialAnswers ?? [])
  const [popup, setPopup] = useState<
    null | { kind: 'graded'; isCorrect: boolean } | { kind: 'subjective' }
  >(null)
  const latestVarsRef = useRef<Record<string, unknown>>({})

  const quiz = useMemo(
    () => (point.quiz ?? {}) as Record<string, unknown>,
    [point.quiz],
  )
  const quizType = String(quiz.type ?? 'multichoice')
  const isSubjective = quizType === 'explanation'
  const answerRevealOnly = isDesignatedReview && isSubjective
  const quizId = String((quiz.quizId as string | number | undefined) ?? point.quizId ?? '')
  const isSubmitDisabled = (!answerRevealOnly && pending.length === 0) || submitting

  const solveResult: QuizSolveResult | null = useMemo(() => {
    if (!submitted) return null
    return {
      correct: submitted.correct,
      corrects: [],
      correctAnswers: submitted.correctAnswers,
      description: submitted.description,
    }
  }, [submitted])

  const handleAnswerChange = useCallback(
    (answers: string[]) => {
      setPending(answers)
      onAnswerChange(answers)
    },
    [onAnswerChange],
  )

  const handleInternalSolve = useCallback(
    (
      _quizId: string | number,
      answers: string[],
      vars: Record<string, unknown>,
    ) => {
      latestVarsRef.current = vars ?? {}
      setPending(answers)
      onAnswerChange(answers)
    },
    [onAnswerChange],
  )

  const handleSubmit = useCallback(async () => {
    if ((!answerRevealOnly && pending.length === 0) || submitting || !quizId) return
    setSubmitting(true)
    setError(null)
    try {
      let isCorrect: boolean | null = null
      if (isLive && componentProgressId != null) {
        const res = await submitPauseQuizResponse(componentProgressId, {
          enrollmentId,
          learningAttemptId,
          quizId,
          answers: answerRevealOnly ? [] : pending,
          pauseTimeMs: point.time,
          vars: latestVarsRef.current,
          answerRevealOnly,
        })
        // 일반 서술형은 자동채점하지 않지만, 지정 복습의 모범답안 확인은 즉시 정답 처리한다.
        isCorrect = isSubjective && !answerRevealOnly ? null : res.isCorrect
        setSubmitted({
          correct: isCorrect,
          correctAnswers: (res.correctAnswers ?? []).map((a) =>
            Array.isArray(a) ? a.join(', ') : String(a ?? ''),
          ),
          description: res.description,
        })
      } else {
        // library-review: 세션/진행 기록 없이 채점만
        const res = await gradeLibraryQuiz({
          quizId,
          submittedResponse: { answers: pending },
          varInstantiation: latestVarsRef.current,
        })
        isCorrect = isSubjective ? null : res.isCorrect
        setSubmitted({
          correct: isCorrect,
          correctAnswers: (res.correctAnswers ?? []).map((a) =>
            Array.isArray(a) ? a.join(', ') : String(a ?? ''),
          ),
          description: res.description,
        })
      }
      // 지정 복습 모범답안 확인은 결과 팝업 없이 바로 풀이를 보여준다.
      setPopup(
        answerRevealOnly
          ? null
          : isCorrect === null
            ? { kind: 'subjective' }
            : { kind: 'graded', isCorrect },
      )
      onSubmitSuccess(quizId)
    } catch (e) {
      setError(getApiErrorMessage(e, t))
    } finally {
      setSubmitting(false)
    }
  }, [
    isLive,
    isSubjective,
    answerRevealOnly,
    componentProgressId,
    enrollmentId,
    learningAttemptId,
    onSubmitSuccess,
    pending,
    point.time,
    quizId,
    submitting,
    t,
  ])

  if (!point.quiz) {
    return (
      <QuizStyleProvider variant="lc">
        <div className="flex h-full items-center gap-3 rounded-3xl bg-lc-bg-primary p-6 shadow-[0_4px_4px_rgba(0,0,0,0.05)]">
          <div
            aria-label="error"
            className="size-8 rounded-full bg-lc-red-900 text-white flex items-center justify-center text-[18px] font-bold shrink-0"
          >
            ✕
          </div>
          <p className="text-[16px] leading-[1.5] tracking-[-0.6px] text-lc-red-dark">
            {t('video.skipLockedFirstWatch')}
          </p>
        </div>
      </QuizStyleProvider>
    )
  }

  return (
    <QuizStyleProvider
      variant="lc"
      headerSuffix={(
        <span className="text-lc-v3-label-md font-bold text-lc-text-tertiary">
          {t('pause.problemLabel', { n: pointIndex + 1 })}
        </span>
      )}
    >
      <div className="lc-pause-quiz-panel relative flex h-full min-h-0 flex-col gap-6 rounded-3xl bg-lc-bg-primary p-6 shadow-[0_4px_4px_rgba(0,0,0,0.05)]">
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
        <QuizRenderer
          key={`${quizId}-${submitted ? 'done' : 'open'}`}
          quizType={quizType}
          quizId={quizId}
          quiz={quiz}
          mode="student"
          useKeypad={false}
          onSolve={answerRevealOnly ? undefined : handleInternalSolve}
          onAnswerChange={answerRevealOnly ? undefined : handleAnswerChange}
          solveResult={solveResult}
          initialAnswers={answerRevealOnly ? undefined : pending.length > 0 ? pending : initialAnswers}
          hideSubmitButton
          hideResultFeedback={isSubjective}
          answerRevealOnly={answerRevealOnly}
          isViewSolved={submitted !== null && submitted.correct !== null}
        />
      </div>

      {error && (
        <div className="flex items-center gap-3 p-3 rounded-lc-md bg-lc-red-100">
          <div className="size-6 rounded-full bg-lc-red-900 text-white flex items-center justify-center text-[14px] font-bold shrink-0">
            ✕
          </div>
          <p className="text-[14px] tracking-[-0.6px] text-lc-red-dark">
            {error}
          </p>
        </div>
      )}

      <div className="flex shrink-0 justify-end">
        {submitted ? (
          <Button variant="navy" size="L" className="w-full" onClick={onResume}>
            {t('pause.resumeVideo')}
          </Button>
        ) : (
          <Button
            variant="primary"
            size="M"
            className="gap-2"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            {!isSubmitDisabled ? <Send className="size-4" aria-hidden /> : null}
            {submitting
              ? t('pause.submitting')
              : answerRevealOnly
                ? t('pause.revealAnswer')
                : t('pause.submit')}
          </Button>
        )}
      </div>
    </div>

    <QuizResultPopup
      isOpen={popup?.kind === 'graded'}
      isCorrect={popup?.kind === 'graded' ? popup.isCorrect : false}
      isCombo={false}
      onClose={() => setPopup(null)}
    />
    <PauseSubjectiveResultPopup
      isOpen={popup?.kind === 'subjective'}
      onClose={() => setPopup(null)}
    />
    </QuizStyleProvider>
  )
}
